import config from 'constants/config';

/**
 * Utility functions for Chrome extension communication with proper error handling
 */

/**
 * Safely send a message to a Chrome extension
 * @param {string} extensionId - The extension ID to send message to
 * @param {object} message - The message to send
 * @param {function} callback - Optional callback function
 * @returns {Promise} - Promise that resolves with the response or rejects with error
 */
export const sendMessageToExtension = (extensionId, message, callback) =>
  new Promise((resolve, reject) => {
    if (!window.chrome?.runtime?.sendMessage) {
      const error = new Error('Chrome extension API not available');
      reject(error);
      return;
    }

    try {
      window.chrome.runtime.sendMessage(extensionId, message, (response) => {
        if (window.chrome.runtime.lastError) {
          const error = new Error(window.chrome.runtime.lastError.message);
          reject(error);
        } else {
          resolve(response);
        }

        // Call the callback if provided
        if (typeof callback === 'function') {
          callback(response, window.chrome.runtime.lastError);
        }
      });
    } catch (error) {
      console.error('Error in sendMessageToExtension:', error);
      reject(error);
    }
  });

/**
 * Send message to multiple extensions with error handling
 * @param {string[]} extensionIds - Array of extension IDs
 * @param {object} message - The message to send
 * @returns {Promise} - Promise that resolves with array of results
 */
export const sendMessageToMultipleExtensions = async (extensionIds, message) => {
  const promises = extensionIds.map((id) => sendMessageToExtension(id, message).catch(() => null));

  const results = await Promise.allSettled(promises);

  // Extract the actual values from the Promise.allSettled results
  const extractedResults = results.map((result) => (result.status === 'fulfilled' ? result.value : null));

  return extractedResults;
};

/**
 * Check if a Chrome extension is installed and accessible
 * @param {string} extensionId - The extension ID to check
 * @returns {Promise<boolean>} - True if extension is accessible
 */
export const isExtensionInstalled = async (extensionId) => {
  try {
    const response = await sendMessageToExtension(extensionId, { message: 'version' });
    return !!response?.version;
  } catch (error) {
    return false;
  }
};

/**
 * Check if multiple extensions are installed
 * @param {string[]} extensionIds - Array of extension IDs to check
 * @returns {Promise<boolean>} - True if at least one extension is accessible
 */
export const isAnyExtensionInstalled = async (extensionIds) => {
  const results = await sendMessageToMultipleExtensions(extensionIds, { message: 'version' });
  return results.some((result) => result?.version);
};

/**
 * Get the plugin IDs from environment variable
 * @returns {string[]} - Array of plugin IDs
 */
export const getPluginIds = () => {
  const { pluginId } = config;
  if (!pluginId) {
    console.warn('REACT_APP_PLUGIN_ID not found in environment variables');
    return [];
  }

  return pluginId.split(' ').filter((id) => id.trim());
};

/**
 * Check if we're in the extension auth popup context
 */
const isExtensionAuthContext = () => {
  const url = new URL(window.location.href);
  const path = window.location.pathname;
  return (
    url.searchParams.get('extensionAuth') === 'true' ||
    path === '/extension/auth' ||
    path.includes('/login') ||
    // Google OAuth redirects to /oauth/google/verify with state=extensionAuth
    // instead of the extensionAuth query param — recognize this as auth context too
    (path.startsWith('/oauth/') && url.searchParams.get('state') === 'extensionAuth')
  );
};

/**
 * Send auth token via DOM element as fallback (NO REDIRECT)
 * Creates DOM element immediately - extension polls for it
 *
 * @param {string} accessToken - The access token to send
 * @param {string} refreshToken - The refresh token to send
 * @param {boolean} [authContextVerified] - If true, skip the URL-based auth context check.
 *   Callers should pass true when they've already verified the auth context before any
 *   async work that might trigger navigation (which would change the URL).
 */
export const sendAuthViaDomFallback = (accessToken, refreshToken, authContextVerified = false) => {
  // Only use fallback in extension auth context (popup window)
  if (!authContextVerified && !isExtensionAuthContext()) {
    return;
  }

  // Remove existing element if any
  const existing = document.getElementById('gp-extension-auth-token');
  if (existing) {
    existing.remove();
  }

  // Create hidden element with token data
  const el = document.createElement('div');
  el.id = 'gp-extension-auth-token';
  el.style.display = 'none';
  el.dataset.accessToken = accessToken;
  if (refreshToken) {
    el.dataset.refreshToken = refreshToken;
  }
  document.body.appendChild(el);

  // Auto-remove element after 30 seconds if extension didn't pick it up
  setTimeout(() => {
    const element = document.getElementById('gp-extension-auth-token');
    if (element) {
      element.remove();
    }
  }, 30000);
};

/**
 * Check if we're in fallback mode - NOT USED ANYMORE (no redirects)
 */
export const isInFallbackMode = () => false;

/**
 * Check if we need to create fallback token element - NOT USED ANYMORE (no redirects)
 */
export const checkAndCreateFallbackElement = () => {
  // No longer needed - element is created immediately without redirect
};

/**
 * Check if the extension response indicates success
 * @param {any} response - Response from extension (can be single value or array)
 * @returns {boolean} - True if response indicates success
 */
const isSuccessResponse = (response) => {
  // Handle array response (from sendMessageToMultipleExtensions)
  if (Array.isArray(response)) {
    return response.some((r) => isSuccessResponse(r));
  }
  // Extension returns 'success' string on successful login
  if (response === 'success') return true;
  // Check for version response (used for version check)
  if (response?.version) return true;
  // Explicit success flag
  if (response?.success === true) return true;
  return false;
};

/**
 * Send message to configured plugins with proper error handling
 * Falls back to DOM element for login messages if extension messaging fails or doesn't respond
 * @param {object} message - The message to send
 * @param {function} callback - Optional callback function
 * @returns {Promise} - Promise that resolves with results
 */
export const sendMessageToPlugins = async (message, callback) => {
  const pluginIds = getPluginIds();

  if (pluginIds.length === 0) {
    const error = new Error('No plugin IDs configured');
    console.warn('REACT_APP_PLUGIN_ID not found in environment variables');
    if (typeof callback === 'function') callback(null, error);
    throw error;
  }

  const isTestMode = config.pluginTest;
  const isLoginMessage = message.message === 'login' && message.accessToken;

  // Capture auth context NOW, before async operations.
  // The URL can change during await (e.g., React router redirect when already logged in),
  // so checking later in the fallback would see the wrong URL and skip DOM element creation.
  const isAuthContext = isLoginMessage && isExtensionAuthContext();

  try {
    let response;
    if (isTestMode && pluginIds.length > 1) {
      response = await sendMessageToMultipleExtensions(pluginIds, message);
    } else {
      response = await sendMessageToExtension(pluginIds[0], message, callback);
    }

    // For login messages, verify the extension actually processed it
    if (isLoginMessage) {
      if (!isSuccessResponse(response)) {
        console.warn('Extension did not confirm login success, response:', response);
        // Use fallback - extension may have received but not processed
        sendAuthViaDomFallback(message.accessToken, message.refreshToken, isAuthContext);
        return { fallback: true, originalResponse: response };
      }
    }

    return response;
  } catch (error) {
    // Fallback: For login messages, store token in DOM for extension to read
    if (isLoginMessage) {
      console.warn('Extension messaging failed, using DOM fallback:', error.message);
      sendAuthViaDomFallback(message.accessToken, message.refreshToken, isAuthContext);
      return { fallback: true };
    }
    throw error;
  }
};
